// Lib
import { connect } from 'react-redux';
import { DragSource } from 'react-dnd';
import { compose } from 'recompose';
import { includes } from 'lodash';

import { ELEMENT_DND_TYPE, ELEMENT_MOVE_OPERATIONS } from '../../../common/elements/elementConstants';

// State
import dragAndDropStateSingleton from './dragAndDropStateSingleton';

// Measurements
import measurementsConnect from '../../components/measurementsStore/measurementsConnect';
import { getMeasurementsMap } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Actions
import { startAttachMode } from '../../utils/dnd/dndActions';
import { duplicateMultipleElements } from '../duplicate/elementDuplicateActions';
import { moveMultipleElements } from '../actions/elementMoveActions';
import { deselectAllElements } from '../selection/selectionActions';
import { dragStart, dragEnd } from '../../reducers/draggingActions';
import { endOperation } from '../../utils/undoRedo/undoRedoActions';
import { getCurrentBoardConnectingElementIdsThunk } from '../connections/elementConnectionsActions';
import { getCanvasZoomStateThunk } from '../../canvas/store/canvasActions';

// Selectors
import { getElements } from '../selectors/elementSelector';
import { getDraggedElementsThunk } from './draggableElementsSelector';
import { getElementChildrenIdsThunk } from '../selectors/elementTraversalSelector';

// Utils
import { getTimestamp } from '../../../common/utils/timeUtil';
import { getChildren, getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getHighestScore, isLocationAttached } from '../../../common/elements/utils/elementLocationUtils';
import { getElementId, getLocationParentId } from '../../../common/elements/utils/elementPropertyUtils';

import { analyticsEvent } from '../../analytics';
import { sendAmplitudeEvent } from '../../analytics/amplitudeService';
import { asObject, getMany, prop, propIn } from '../../../common/utils/immutableHelper';
import buildMovedElementOffsetsMap from './utils/buildMovedElementOffsetsMap';
import buildElementMovesArray from './utils/buildElementMovesArray';
import { getScaledGrabOffset } from '../../utils/dnd/dragAndDropUtils';
import {
    getMeasurementsCache,
    setMeasurementsCache,
} from '../../components/measurementsStore/elementMeasurements/measurementsCacheSingleton';
import * as pointsLib from '../../../common/maths/geometry/point';
import { isWorkspaceSectionCanvas } from '../../workspace/workspacePropertyUtils';

import { isTask, isTaskList } from '../../../common/elements/utils/elementTypeUtils';
import { isIpad } from '../../utils/services/browser';
import areDraggedItemsTaskListsWithoutHeadings from './task/areDraggedItemsTaskListsWithoutHeadings';
import buildTaskListOntoTasksMovesArray from './utils/buildTaskListOntoTasksMovesArray';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';

// Constants
import { BoardSections } from '../../../common/boards/boardConstants';
import { MODIFIER_KEYS } from '../../utils/dnd/modifierKeys/dragModifierKeysConstants';
import { EVENT_TYPE_NAMES } from '../../../common/analytics/amplitudeEventTypesUtil';
import { ATTACH_MODE_TYPES } from '../../utils/dnd/dndConstants';

/**
 * Common functionality shared between the ElementContainer and the Task to determine whether the element is
 * actually draggable.
 */
export const shouldAttachDragSource = ({
    isEditable,
    isEditing,
    isEditingChild,
    documentMode,
    isFocusedForegroundElement,
}) => isEditable && !isEditing && !isEditingChild && !documentMode && !isFocusedForegroundElement;

const mapDispatchToProps = (dispatch) => ({
    startDragging: (isSelected, ids) => {
        if (!isSelected) {
            dispatch(deselectAllElements());
        }
        dispatch(dragStart({ ids }));
    },
    endDragging: (dropState) => dispatch(dragEnd(dropState)),
    dispatchEndMoveOperation: () => dispatch(endOperation('move')),
    onDropMove: ({ moves, initialMeasurements, transactionId, sync = true }) =>
        dispatch(
            moveMultipleElements({
                moves,
                initialMeasurements,
                transactionId,
                sync,
                moveOperation: ELEMENT_MOVE_OPERATIONS.DROP,
            }),
        ),
    onDropCopy: ({ moves, transactionId }) => dispatch(duplicateMultipleElements({ moves, transactionId })),
    dispatchGetDraggedElements: ({ grabbedElement, permissions }) =>
        dispatch(getDraggedElementsThunk({ grabbedElement, permissions })),
    dispatchGetDraggedChildIds: (elementIds) => dispatch(getElementChildrenIdsThunk(elementIds)),
    dispatchGetCurrentElements: () => dispatch((_, getState) => getElements(getState())),
    dispatchGetConnectedElementIds: (elementIds) => dispatch(getCurrentBoardConnectingElementIdsThunk({ elementIds })),
    dispatchStartAttachMode: (hoveredElementId) =>
        dispatch(startAttachMode(hoveredElementId, ATTACH_MODE_TYPES.HOT_SPOT)),
    dispatchGetCanvasZoomState: () => dispatch(getCanvasZoomStateThunk()),
});

const mapMeasurementsDispatchToProps = (dispatch) => ({
    dispatchGetMeasurementsMap: () => dispatch((_, getState) => getMeasurementsMap(getState())),
});

const trackDraggingAnalyticsEvent = (copyDrag, dropResult, { element, inList }) => {
    if (copyDrag) return analyticsEvent('duplicated-element-opt-drag');
    if (dropResult.location.section === BoardSections.TRASH) return analyticsEvent('moved-element-to-trash-drag');
    if (
        dropResult.location.section === BoardSections.INBOX &&
        dropResult.location.parentId === element.getIn(['location', 'parentId']) &&
        inList !== 'in-list'
    ) {
        return analyticsEvent('moved-element-to-unsorted-notes');
    }
    if (isTask(element)) sendAmplitudeEvent({ eventType: EVENT_TYPE_NAMES.DRAGGED_TO_DO_ITEM });
};

/**
 * Drag source contract.
 */
const draggableElementSource = {
    canDrag({ isEditing, isEditingChild, isEditable, isLocked }) {
        return !isLocked && isEditable && !(isEditing || isEditingChild);
    },

    // Returns the data describing the dragged tool
    beginDrag(props, monitor, component) {
        const {
            attachment,
            element: grabbedElement,
            startDragging,
            isSelected,
            dispatchGetDraggedElements,
            dispatchGetDraggedChildIds,
            dispatchGetConnectedElementIds,
            dispatchStartAttachMode,
            dispatchGetMeasurementsMap,
            dispatchGetCanvasZoomState,
            gridSize,
            permissions,
            getContextZoomScale = () => 1,
        } = props;

        const zoomState = dispatchGetCanvasZoomState();
        const zoomScale = prop('scale', zoomState);
        const zoomTranslationPx = prop('translation', zoomState);
        const zoomScaleOnDragSource = getContextZoomScale();

        const sourceClientOffset = monitor.getSourceClientOffset();
        const clientOffset = monitor.getClientOffset();
        const scaledGrabOffset = getScaledGrabOffset({ clientOffset, sourceClientOffset });
        const unscaledGrabOffset = pointsLib.reverseScale(zoomScaleOnDragSource, scaledGrabOffset);

        dragAndDropStateSingleton.scaledGrabOffset = scaledGrabOffset;

        // Find out the offsets (from the top left of the grabbed element) for all dragged elements
        const draggedElements = dispatchGetDraggedElements({ grabbedElement, permissions });
        const draggedElementIds = draggedElements.map(getElementId);

        const originalMeasurementsMap = dispatchGetMeasurementsMap();

        // In order to get an accurate measurements before dragging, recalculate all elements
        const measurementsMap = [grabbedElement, ...draggedElements].reduce((acc, element) => {
            const elementId = getElementId(element);

            const elementMeasurement = asObject(prop(elementId, originalMeasurementsMap));

            // If element is not on canvas, the measurements are usually not accurate after a scroll, so remeasure it.
            acc[elementId] = isWorkspaceSectionCanvas(elementMeasurement)
                ? elementMeasurement
                : measurementsRegistry.getElementMeasurementFromCanvasDocument(elementId);

            return acc;
        }, {});

        // Currently can only start in attach mode if dragging a single element
        // NOTE: Assumes the attach mode is a hot spot right now
        if (draggedElements?.length === 1 && draggedElements.every(isLocationAttached)) {
            const parentId = getLocationParentId(draggedElements[0]);
            dispatchStartAttachMode(parentId);
        }

        const draggedChildIds = dispatchGetDraggedChildIds(draggedElementIds);

        const draggedDescendantIds = [...draggedElementIds, ...draggedChildIds];

        const connectingElementIds = dispatchGetConnectedElementIds(draggedDescendantIds);
        const location = grabbedElement.get('location').toJS();

        startDragging(isSelected, draggedElementIds);

        // Save the measurements map so on drop, if the measurements don't initially exist (as the board
        // has changed), the lines can use the cached measurements instead.
        setMeasurementsCache(measurementsMap);

        // Find out the offsets (from the top left of the grabbed element) for all dragged elements
        const unscaledElementOffsetsMap = buildMovedElementOffsetsMap({
            grabbedElement,
            draggedElements,
            measurementsMap,
            gridSize,
            unscaledGrabOffset,
            zoomScale,
            zoomScaleOnDragSource,
            zoomTranslationPx,
        });

        const unscaledCustomDragOffset = isIpad() ? pointsLib.scale(gridSize, { x: 0, y: -1 }) : { x: 0, y: 0 };
        const scaledCustomDragOffset = pointsLib.scale(zoomScaleOnDragSource, unscaledCustomDragOffset);

        return {
            dragStartTimestamp: getTimestamp(),
            attachment,
            element: grabbedElement,
            connectingElementIds,
            draggedElementIds,
            draggedDescendantIds,
            draggedElements,
            scaledCustomDragOffset,
            unscaledElementOffsetsMap,
            zoomScaleOnDragSource,
            originalLocation: location,
            fromList: !!props.inList,
            hoveredItems: {},
            hoveredTypes: {},
        };
    },

    // FIXME memoising this might reduce javascript load while dragging, on pages with many elements
    isDragging(props, monitor) {
        const { element } = props;
        const thisElementId = getElementId(element);
        const item = monitor.getItem();
        const { draggedElementIds, connectingElementIds } = item;
        return draggedElementIds.includes(thisElementId) || includes(connectingElementIds, thisElementId);
    },

    // Handles when the tool is dropped
    endDrag(props, monitor, component) {
        const {
            inTrash,
            element,
            endDragging,
            onDropMove,
            onDropCopy,
            dragModifierKeys,
            gridSize,
            currentBoardId,
            dispatchEndMoveOperation,
            dispatchGetMeasurementsMap,
            dispatchGetCurrentElements,
        } = props;

        const operationTransactionId = dispatchEndMoveOperation();

        // Doing nothing if it was not dropped onto a compatible target
        if (!monitor.didDrop()) return endDragging();

        const item = monitor.getItem();
        const dropResult = monitor.getDropResult();
        const { sync, dropState } = dropResult;

        const droppedElementIds = item.draggedElementIds;

        const copyDrag = !inTrash && dragModifierKeys.get(MODIFIER_KEYS.altKey);

        // A successful drag should store some dropState
        dropState && !copyDrag
            ? endDragging({ ...dragAndDropStateSingleton, ...dropState, droppedElementIds })
            : endDragging();

        // Only drop if a location is returned by the drop target
        if (!dropResult.location) return;

        trackDraggingAnalyticsEvent(copyDrag, dropResult, props);

        // Use the drop transaction ID if one exists, otherwise use the move transaction ID
        const transactionId = dropResult.transactionId || operationTransactionId;

        const measurementsMap = dispatchGetMeasurementsMap();
        const elements = dispatchGetCurrentElements();
        const currentBoardElements = getChildren(elements, currentBoardId).valueSeq().toJS();
        const filteredElements = currentBoardElements.filter((el) => item.draggedElementIds.indexOf(el.id) === -1);
        const highestScore = getHighestScore(filteredElements);

        const dropLocationParentId = propIn(['location', 'parentId'], dropResult);
        const dropLocationParentElement = getElement(elements, dropLocationParentId);

        const isTaskListDropOnTasks =
            (isTaskList(dropLocationParentElement) || isTask(dropLocationParentElement)) &&
            areDraggedItemsTaskListsWithoutHeadings(props, monitor);

        const moves = isTaskListDropOnTasks
            ? buildTaskListOntoTasksMovesArray({ elements, dropResult, measurementsMap })
            : buildElementMovesArray({ element, dropResult, item, gridSize, measurementsMap, highestScore });

        if (copyDrag) return onDropCopy({ moves, transactionId });

        const measurementsCache = getMeasurementsCache();
        const initialMeasurements = asObject(getMany(droppedElementIds, measurementsCache));

        onDropMove({ moves, initialMeasurements, transactionId, sync });
    },
};

const dragCollect = (connector, monitor) => ({
    // Call inside render() to let React handle the drag event
    connectDragSource: connector.dragSource(),
    connectDragPreview: connector.dragPreview(),
    // Get current drag state from the monitor
    isDragging: monitor.isDragging(),
});

export default (Element) =>
    compose(
        connect(null, mapDispatchToProps),
        measurementsConnect(null, mapMeasurementsDispatchToProps),
        DragSource(ELEMENT_DND_TYPE, draggableElementSource, dragCollect),
    )(Element);
